import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isUserSignupEnabled } from '../../config/featureFlags';
import './Header.css';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const base = location.pathname.startsWith('/m') ? '/m' : '/';
  const withBase = (path) => (base === '/' ? path : `${base}${path}`);
  const onMobileRoutes = base === '/m';

  // Handle scroll effect for navigation (no-op on mobile shell)
  useEffect(() => {
    if (onMobileRoutes) return; // guard: don't attach listeners in mobile shell
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen, onMobileRoutes]);

  // Handle navigation to sections from URL hash (desktop only)
  useEffect(() => {
    if (onMobileRoutes) return;
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.substring(1);
      const element = document.getElementById(sectionId);
      if (element) {
        setTimeout(() => {
          // Scroll so the section appears at the top of the viewport.
          // Compensate for the header height so the section isn't hidden beneath it.
          const header = document.querySelector('nav.landing-nav') || document.querySelector('nav');
          const headerHeight = header ? header.offsetHeight : 0;
          const top = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location, onMobileRoutes]);

  // If we’re under /m, MobileShell will render MobileHeader. Do not render desktop Header.
  if (onMobileRoutes) return null;

  // Handle logo click to navigate to landing (respecting mobile base)
  const landingPath = base === '/' ? '/' : base;
  const handleLogoClick = (e) => {
    // if already on the landing path, just scroll to top
    if (location.pathname === landingPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // navigate to landing (preserve smooth behavior on the new page)
      e.preventDefault();
      navigate(landingPath);
    }
  };

  // Determine which header to show based on route
  const isLoginPage = location.pathname === '/login' || location.pathname === '/m/login';
  const isSignupPage = location.pathname === '/register' || location.pathname === '/signup' || location.pathname === '/m/register' || location.pathname === '/m/signup';
  const isAuthPage = isLoginPage || isSignupPage;

  // If on login/signup pages, show minimal header
  if (isAuthPage) {
    return (
      <nav className="minimal-nav">
        <div className="nav-container">
          <Link to={landingPath} className="brand" onClick={handleLogoClick}>
            <span className="brand-icon-wrap">
              <img src="/logos/blue_gold_head.png" alt="DocuHounds logo" className="brand-icon-img" />
            </span>
            <span className="brand-wordmark-wrap">
              <img src="/logos/blue_docuhounds_text.png" alt="DocuHounds" className="brand-wordmark-img" />
            </span>
          </Link>
        </div>
      </nav>
    );
  }

  // Default: show marketing header for public pages (regardless of auth status)
  // This header is now used for ALL public routes (/, /about, /blog, etc.)
  // even when the user is logged in. The AppLayout will render Navbar for app routes.
  const isOnLandingPage = location.pathname === landingPath;
  
  // Handle navigation to landing page sections
  const handleSectionNavigation = (sectionId) => {
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
    if (isOnLandingPage) {
      // If already on landing page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        // Compute header height and scroll so the section top appears below the header
        const header = document.querySelector('nav.landing-nav') || document.querySelector('nav');
        const headerHeight = header ? header.offsetHeight : 0;
        const top = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    } else {
      // If on another page, navigate to landing page and pass the target
      // section in navigation state. LandingPage will read this and scroll
      // after it mounts so the element exists in the DOM.
      navigate(landingPath, { state: { scrollTo: sectionId } });
    }
  };
  
  return (
    <nav className={`landing-nav ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
  <Link to={landingPath} className="brand" onClick={handleLogoClick}>
          <span className="brand-icon-wrap">
            <img src="/logos/blue_gold_head.png" alt="DocuHounds logo" className="brand-icon-img" />
          </span>
          <span className="brand-wordmark-wrap">
            <img src="/logos/blue_docuhounds_text.png" alt="DocuHounds" className="brand-wordmark-img" />
          </span>
        </Link>
        
        {/* Desktop Navigation */}
        <div className="nav-links desktop-nav">
          <a href="#how-it-works" onClick={(e) => { e.preventDefault(); handleSectionNavigation('how-it-works'); }}>
            How It Works
          </a>
          <a href="#features" onClick={(e) => { e.preventDefault(); handleSectionNavigation('features'); }}>
            Features
          </a>
          <Link to={withBase('/blog')}>Blog</Link>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); handleSectionNavigation('pricing'); }}>
            Pricing
          </a>
          <Link to={withBase('/about')}>About Us</Link>
        </div>
        
        {/* Desktop Actions */}
        <div className="nav-actions desktop-actions">
          <Link
            to={user ? '/dashboard' : withBase('/login')}
            className="login-btn"
            onClick={(e) => {
              // If already logged in, navigate to dashboard
              if (user) {
                e.preventDefault();
                navigate('/dashboard');
              }
            }}
          >
            {user ? 'Dashboard' : 'Login'}
          </Link>
          <Link
            to={isUserSignupEnabled() ? withBase('/register') : '#'}
            className="signup-btn"
            onClick={(e) => {
              if (!isUserSignupEnabled()) {
                e.preventDefault();
                // Lightweight UX notice (typo kept as specified by requirements)
                alert('Comming Soon');
              }
            }}
          >
            Start Free
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-nav-links">
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); handleSectionNavigation('how-it-works'); }}>
              How It Works
            </a>
            <a href="#features" onClick={(e) => { e.preventDefault(); handleSectionNavigation('features'); }}>
              Features
            </a>
            <Link to={withBase('/blog')} onClick={() => setIsMobileMenuOpen(false)}>Blog</Link>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); handleSectionNavigation('pricing'); }}>
              Pricing
            </a>
            <Link to={withBase('/about')} onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
          </div>
          <div className="mobile-nav-actions">
            <Link
              to={isUserSignupEnabled() ? withBase('/register') : '#'}
              className="signup-btn"
              onClick={(e) => {
                if (!isUserSignupEnabled()) {
                  e.preventDefault();
                  alert('Comming Soon');
                }
                setIsMobileMenuOpen(false);
              }}
            >
              Sign Up
            </Link>
            <Link
              to={user ? '/dashboard' : withBase('/login')}
              className="login-btn"
              onClick={(e) => {
                setIsMobileMenuOpen(false);
                // If already logged in, navigate to dashboard
                if (user) {
                  e.preventDefault();
                  navigate('/dashboard');
                }
              }}
            >
              {user ? 'Dashboard' : 'Login'}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Header;
